import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const FILE_ICONS = {
  py: '🐍', js: '🟨', ts: '🔷', jsx: '⚛️', tsx: '⚛️',
  html: '🌐', css: '🎨', json: '📋', md: '📖', txt: '📄',
  csv: '📊', xlsx: '📊', pdf: '📄', zip: '📦', tar: '📦',
  mp4: '🎬', mp3: '🎵', jpg: '🖼️', jpeg: '🖼️', png: '🖼️',
  webp: '🖼️', gif: '🖼️', sql: '🗃️', sh: '⚙️', c: '⚙️',
  cpp: '⚙️', h: '⚙️', java: '☕', rs: '🦀', go: '🐹',
  rb: '💎', php: '🐘', xml: '📋', yml: '⚙️', yaml: '⚙️',
  env: '🔑', gitignore: '🙈', dockerfile: '🐳',
};

const TEXT_TYPES = ['py', 'js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'md', 'txt', 'csv', 'yml', 'yaml', 'sh', 'c', 'cpp', 'h', 'java', 'rs', 'go', 'rb', 'php', 'xml', 'sql', 'env', 'gitignore', 'dockerfile', ''];
const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

function getFileIcon(type) { return FILE_ICONS[type?.toLowerCase()] || '📄'; }
function formatSize(b) {
  if (!b) return '0B';
  if (b < 1024) return `${b}B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1048576).toFixed(1)}MB`;
}

// ─── TREE ─────────────────────────────────────────────────

function TreeNode({ name, path, allFolders, allFiles, currentFolder, onNavigate, depth = 0 }) {
  const [open, setOpen] = useState(path === '/' || currentFolder.startsWith(path));
  const children = allFolders.filter(f => {
    if (path === '/') return f.split('/').filter(Boolean).length === 1;
    return f.startsWith(path + '/') && f.replace(path + '/', '').split('/').filter(Boolean).length === 1;
  });
  const filesHere = allFiles.filter(f => (f.folder || '/') === path);
  const isActive = currentFolder === path;

  return (
    <div>
      <div onClick={() => { setOpen(!open); onNavigate(path); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: `5px 12px 5px ${12 + depth * 16}px`, cursor: 'pointer', borderRadius: '6px', margin: '1px 6px', background: isActive ? 'rgba(43,108,176,0.12)' : 'transparent', transition: 'background .15s' }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: '10px', color: '#718096', width: '10px', flexShrink: 0 }}>{children.length > 0 ? (open ? '▾' : '▸') : ''}</span>
        <span style={{ fontSize: '14px' }}>📁</span>
        <span style={{ fontSize: '12px', fontWeight: isActive ? 700 : 500, color: isActive ? '#93C5FD' : '#CBD5E0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </div>
      {open && (
        <div>
          {children.map(child => {
            const childName = child.split('/').filter(Boolean).pop();
            return <TreeNode key={child} name={childName} path={child} allFolders={allFolders} allFiles={allFiles} currentFolder={currentFolder} onNavigate={onNavigate} depth={depth + 1} />;
          })}
          {filesHere.map(file => (
            <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: `4px 12px 4px ${28 + depth * 16}px`, margin: '1px 6px', borderRadius: '6px' }}>
              <span style={{ fontSize: '13px' }}>{getFileIcon(file.file_type)}</span>
              <span style={{ fontSize: '12px', color: '#A0AEC0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.original_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CODE VIEWER ──────────────────────────────────────────

function CodeViewer({ file, content, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const lines = content.split('\n');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#1A2332' }}>
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '0 16px', background: '#1e2a3a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px 10px 0', borderBottom: '2px solid #4299E1', marginBottom: '-1px' }}>
          <span style={{ fontSize: '14px' }}>{getFileIcon(file.file_type)}</span>
          <span style={{ fontSize: '13px', color: '#E2E8F0', fontWeight: 500 }}>{file.original_name}</span>
          <span style={{ fontSize: '11px', color: '#718096', background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: '6px' }}>{file.file_type?.toUpperCase()}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: '#718096' }}>{lines.length} lignes · {formatSize(file.size)}</span>
          <button onClick={copy} style={{ background: copied ? '#68D391' : 'rgba(255,255,255,0.08)', color: copied ? '#1A2332' : '#A0AEC0', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
            {copied ? '✓ Copié' : '📋 Copier'}
          </button>
          <a href={`https://social-agora.com${file.filename}`} download style={{ background: 'rgba(255,255,255,0.08)', color: '#A0AEC0', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, textDecoration: 'none' }}>⬇️ Télécharger</a>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', color: '#A0AEC0', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>×</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: '13px', lineHeight: 1.7 }}>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ padding: '0 20px 0 16px', color: '#4A5568', userSelect: 'none', textAlign: 'right', minWidth: '48px', fontSize: '12px', verticalAlign: 'top', paddingTop: '1px' }}>{i + 1}</td>
                <td style={{ padding: '1px 24px 1px 16px', color: '#E2E8F0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', verticalAlign: 'top' }}>{line || ' '}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────

export default function ProjectFiles() {
  const { id } = useParams();
  const { user, token, API } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState('/');
  const [files, setFiles] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [loadingFile, setLoadingFile] = useState(null);
  const [renamingFile, setRenamingFile] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const canEdit = project?.members?.some(m => m.user.username === user?.username && ['fondateur', 'archon'].includes(m.role));
  const isMember = project?.members?.some(m => m.user.username === user?.username);

  const fetchProject = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/projects/${id}`, { headers });
      setProject(res.data);
      setAllFiles(res.data.files || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [API, id, token]);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${id}/files?folder=${encodeURIComponent(currentFolder)}`);
      setFiles(res.data);
    } catch (err) { console.error(err); }
  }, [API, id, currentFolder]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${id}/folders`);
      setAllFolders(res.data);
    } catch (err) { console.error(err); }
  }, [API, id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);
  useEffect(() => { fetchFiles(); }, [fetchFiles]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const refresh = () => { fetchFiles(); fetchFolders(); fetchProject(); };

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/projects/${id}/files?folder=${encodeURIComponent(currentFolder)}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      refresh();
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const folderPath = currentFolder === '/' ? `/${newFolderName.trim()}` : `${currentFolder}/${newFolderName.trim()}`;
    setAllFolders(prev => [...prev, folderPath].sort());
    setNewFolderName(''); setShowNewFolder(false);
    setCurrentFolder(folderPath);
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/projects/${id}/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (viewingFile?.id === fileId) { setViewingFile(null); setViewingContent(null); }
      refresh();
    } catch (err) { console.error(err); }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Supprimer le dossier "${folder}" et tous ses fichiers ?`)) return;
    try {
      await axios.delete(`${API}/projects/${id}/folders?folder=${encodeURIComponent(folder)}`, { headers: { Authorization: `Bearer ${token}` } });
      setAllFolders(prev => prev.filter(f => !f.startsWith(folder)));
      if (currentFolder.startsWith(folder)) setCurrentFolder('/');
      else refresh();
    } catch (err) { console.error(err); }
  };

  const handleRename = async (fileId) => {
    if (!renameValue.trim()) return;
    try {
      await axios.put(`${API}/projects/${id}/files/${fileId}/rename?name=${encodeURIComponent(renameValue.trim())}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (viewingFile?.id === fileId) setViewingFile(prev => ({ ...prev, original_name: renameValue.trim() }));
      setRenamingFile(null); setRenameValue('');
      refresh();
    } catch (err) { console.error(err); }
  };

  const handleOpenFile = async (file) => {
    if (IMAGE_TYPES.includes(file.file_type?.toLowerCase())) {
      setViewingFile(file);
      setViewingContent({ type: 'image', url: `https://social-agora.com${file.filename}` });
      return;
    }
    if (!TEXT_TYPES.includes(file.file_type?.toLowerCase())) {
      window.open(`https://social-agora.com${file.filename}`, '_blank');
      return;
    }
    setLoadingFile(file.id);
    try {
      const res = await axios.get(`${API}/projects/${id}/files/${file.id}/content`);
      setViewingFile(file);
      setViewingContent({ type: 'code', content: res.data.content });
    } catch (err) { console.error(err); }
    finally { setLoadingFile(null); }
  };

  const breadcrumbs = currentFolder === '/' ? [] : currentFolder.split('/').filter(Boolean);
  const subFolders = allFolders.filter(f => {
    if (currentFolder === '/') return f.split('/').filter(Boolean).length === 1;
    return f.startsWith(currentFolder + '/') && f.replace(currentFolder + '/', '').split('/').filter(Boolean).length === 1;
  });

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#718096', background: '#0d1117', minHeight: '100vh' }}>Chargement...</div>;
  if (!project) return <div style={{ textAlign: 'center', padding: '80px', color: '#718096', background: '#0d1117', minHeight: '100vh' }}>Projet introuvable.</div>;

  return (
    <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>

      {/* Top bar */}
      <div style={{ background: '#161b22', borderBottom: '1px solid #30363d', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
        <Link to={`/projects/${id}`} style={{ color: '#58a6ff', fontSize: '13px', fontWeight: 500, textDecoration: 'none' }}>
          ← {project.emoji || '🚀'} {project.title}
        </Link>
        <span style={{ color: '#484f58', fontSize: '13px' }}>/</span>
        <span style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>📁 Fichiers</span>
        <span style={{ fontSize: '11px', color: '#484f58', background: '#21262d', padding: '2px 8px', borderRadius: '10px' }}>
          {allFiles.length} fichier{allFiles.length > 1 ? 's' : ''}
        </span>
        {(isMember || canEdit) && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {canEdit && (
              <button onClick={() => setShowNewFolder(!showNewFolder)} style={{ background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                📁 Nouveau dossier
              </button>
            )}
            {canEdit && (
              <label style={{ cursor: uploading ? 'wait' : 'pointer' }}>
                <input type="file" multiple onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
                <span style={{ background: '#238636', color: '#fff', border: '1px solid #2ea043', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                  {uploading ? 'Upload...' : '⬆️ Ajouter des fichiers'}
                </span>
              </label>
            )}
          </div>
        )}
      </div>

      {/* New folder input */}
      {showNewFolder && canEdit && (
        <div style={{ background: '#161b22', borderBottom: '1px solid #30363d', padding: '8px 20px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '16px' }}>📁</span>
          <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nom du dossier..."
            onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
            autoFocus
            style={{ flex: 1, maxWidth: '300px', padding: '6px 12px', border: '1px solid #30363d', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#0d1117', color: '#e6edf3' }}
            onFocus={e => e.target.style.borderColor = '#58a6ff'} onBlur={e => e.target.style.borderColor = '#30363d'}
          />
          <button onClick={handleCreateFolder} style={{ background: '#238636', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Créer</button>
          <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} style={{ background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Annuler</button>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Tree sidebar */}
        <div style={{ width: '260px', flexShrink: 0, background: '#161b22', borderRight: '1px solid #21262d', overflowY: 'auto', padding: '8px 0' }}>
          <div style={{ padding: '8px 12px 4px', fontSize: '11px', fontWeight: 600, color: '#484f58', letterSpacing: '1px' }}>ARBORESCENCE</div>
          <TreeNode name={project.title} path="/" allFolders={allFolders} allFiles={allFiles} currentFolder={currentFolder}
            onNavigate={(path) => { setCurrentFolder(path); setViewingFile(null); setViewingContent(null); }} depth={0}
          />
        </div>

        {/* Content */}
        {viewingFile && viewingContent ? (
          viewingContent.type === 'code' ? (
            <CodeViewer file={viewingFile} content={viewingContent.content} onClose={() => { setViewingFile(null); setViewingContent(null); }} />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#1A2332', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px' }}>{getFileIcon(viewingFile.file_type)}</span>
                <span style={{ fontSize: '13px', color: '#E2E8F0', fontWeight: 500 }}>{viewingFile.original_name}</span>
                <button onClick={() => { setViewingFile(null); setViewingContent(null); }} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.08)', color: '#A0AEC0', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: '20px' }}>
                <img src={viewingContent.url} alt={viewingFile.original_name} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }} />
              </div>
            </div>
          )
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#0d1117' }}>

            {/* Breadcrumb */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', background: '#161b22' }}>
              <button onClick={() => setCurrentFolder('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: breadcrumbs.length > 0 ? '#58a6ff' : '#e6edf3', fontWeight: 600, padding: '2px 4px' }}>
                {project.emoji || '🚀'} {project.title}
              </button>
              {breadcrumbs.map((part, i) => {
                const path = '/' + breadcrumbs.slice(0, i + 1).join('/');
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={i}>
                    <span style={{ color: '#484f58', fontSize: '13px' }}>/</span>
                    <button onClick={() => setCurrentFolder(path)} style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', fontSize: '13px', color: isLast ? '#e6edf3' : '#58a6ff', fontWeight: 600, padding: '2px 4px' }}>{part}</button>
                  </React.Fragment>
                );
              })}
            </div>

            {/* File table */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #21262d' }}>
                    <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#484f58', width: '40px' }}></th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#484f58' }}>Nom</th>
                    <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#484f58', width: '80px' }}>Taille</th>
                    <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#484f58', width: '120px' }}>Uploadé par</th>
                    <th style={{ width: '120px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Back */}
                  {currentFolder !== '/' && (
                    <tr style={{ borderBottom: '1px solid #21262d', cursor: 'pointer' }}
                      onClick={() => { const parts = currentFolder.split('/').filter(Boolean); parts.pop(); setCurrentFolder(parts.length === 0 ? '/' : '/' + parts.join('/')); }}
                      onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '10px 20px', fontSize: '16px' }}>⬆️</td>
                      <td style={{ padding: '10px 8px', fontSize: '13px', color: '#8b949e' }} colSpan={4}>..</td>
                    </tr>
                  )}

                  {/* Folders */}
                  {subFolders.map(folder => {
                    const name = folder.split('/').filter(Boolean).pop();
                    const filesInFolder = allFiles.filter(f => (f.folder || '/').startsWith(folder));
                    return (
                      <tr key={folder} style={{ borderBottom: '1px solid #21262d' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer' }} onClick={() => setCurrentFolder(folder)}>📁</td>
                        <td style={{ padding: '10px 8px', cursor: 'pointer' }} onClick={() => setCurrentFolder(folder)}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#58a6ff' }}>{name}</span>
                          <span style={{ fontSize: '11px', color: '#484f58', marginLeft: '8px' }}>{filesInFolder.length} fichier{filesInFolder.length > 1 ? 's' : ''}</span>
                        </td>
                        <td style={{ padding: '10px 20px', textAlign: 'right', fontSize: '12px', color: '#484f58' }}>—</td>
                        <td style={{ padding: '10px 20px', fontSize: '12px', color: '#484f58' }}>—</td>
                        <td style={{ padding: '10px 20px', textAlign: 'center' }}>
                          {canEdit && <button onClick={() => handleDeleteFolder(folder)} style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: '14px' }} title="Supprimer">🗑️</button>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Files */}
                  {files.map(file => {
                    const isText = TEXT_TYPES.includes(file.file_type?.toLowerCase());
                    const isImage = IMAGE_TYPES.includes(file.file_type?.toLowerCase());
                    const canOpen = isText || isImage;
                    const isRenaming = renamingFile === file.id;

                    return (
                      <tr key={file.id} style={{ borderBottom: '1px solid #21262d' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 20px', fontSize: '16px' }}>{loadingFile === file.id ? '⏳' : getFileIcon(file.file_type)}</td>
                        <td style={{ padding: '10px 8px' }}>
                          {isRenaming ? (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <input value={renameValue} onChange={e => setRenameValue(e.target.value)} autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleRename(file.id); if (e.key === 'Escape') { setRenamingFile(null); setRenameValue(''); } }}
                                style={{ padding: '4px 10px', border: '1px solid #58a6ff', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#0d1117', color: '#e6edf3', width: '200px' }}
                              />
                              <button onClick={() => handleRename(file.id)} style={{ background: '#238636', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>OK</button>
                              <button onClick={() => { setRenamingFile(null); setRenameValue(''); }} style={{ background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Annuler</button>
                            </div>
                          ) : (
                            <span onClick={canOpen ? () => handleOpenFile(file) : undefined} style={{ fontSize: '13px', fontWeight: 500, color: canOpen ? '#58a6ff' : '#c9d1d9', cursor: canOpen ? 'pointer' : 'default' }}>
                              {file.original_name}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 20px', textAlign: 'right', fontSize: '12px', color: '#8b949e' }}>{formatSize(file.size)}</td>
                        <td style={{ padding: '10px 20px', fontSize: '12px', color: '#8b949e' }}>{file.uploaded_by.username}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            {canEdit && !isRenaming && (
                              <button onClick={() => { setRenamingFile(file.id); setRenameValue(file.original_name); }} style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: '13px', padding: '2px 6px', borderRadius: '4px' }} title="Renommer">✏️</button>
                            )}
                            <a href={`https://social-agora.com${file.filename}`} download style={{ color: '#484f58', fontSize: '13px', textDecoration: 'none', padding: '2px 6px' }} title="Télécharger">⬇️</a>
                            {canEdit && !isRenaming && (
                              <button onClick={() => handleDeleteFile(file.id)} style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: '13px', padding: '2px 6px', borderRadius: '4px' }} title="Supprimer">🗑️</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {subFolders.length === 0 && files.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '48px', textAlign: 'center', color: '#484f58' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📂</div>
                        <div style={{ fontSize: '13px' }}>Dossier vide{isMember ? ' — uploadez des fichiers' : ''}</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
